import { createTypes, completeTypes, withPostSuccess, withPostFailure } from 'redux-recompose';

import DeliveryService from '~services/Delivery/service';
import { getMinDeliveryOption } from '~utils/delivery';

import { ALL_DELIVERIES } from './constants';

export const actions = createTypes(
  completeTypes([
    'GET_DELIVERY_ESTIMATES',
    'CLEAR_DELIVERY_ESTIMATES',
    'SET_DELIVERY_ITEMS',
    'SET_DELIVERY_LOADING',
    'SET_DELIVERY_TYPE_SELECTED',
    'SET_UPDATING_DELIVERY_ORDER',
    'SET_EXPIRED_STATUS',
    'CANCEL_DELIVERY'
  ]),
  '@@CORE'
);

const targets = {
  DELIVERY_OPTIONS: 'deliveryOptions',
  DELIVERY_ITEMS: 'deliveryItems',
  DELIVERY_TYPE_SELECTED: 'deliveryTypeSelected',
  ORDERS_UPDATING_DELIVERY: 'ordersUpdatingDelivery',
  CANCEL_DELIVERY: 'cancelDelivery'
};

const actionCreators = {
  setDeliveryItems: (item) => (dispatch, getState) => {
    const state = getState();
    const { deliveryItems } = state.delivery;

    if (deliveryItems.some((order) => order.id === item.id)) {
      return;
    }

    dispatch({
      type: actions.SET_DELIVERY_ITEMS,
      target: targets.DELIVERY_ITEMS,
      payload: item
    });
  },
  getDeliveriesEstimates: ({ orderId, requestId, deliveryTypes, retryDelivery }) => (dispatch, getState) => {
    const storeId = getState().core.storeSelected;
    const orderKey = `order${orderId}`;
    dispatch(actionCreators.setDeliveryLoading(retryDelivery ? deliveryTypes[0] : ALL_DELIVERIES, true));

    Promise.allSettled(
      deliveryTypes.map((type) => {
        return new Promise((res, rej) => {
          dispatch({
            type: actions.GET_DELIVERY_ESTIMATES,
            payload: { storeId, orderId, type },
            service: DeliveryService.getDeliveryEstimate,
            target: targets.DELIVERY_OPTIONS,
            successSelector: ({ data }) => {
              const state = getState();
              const { deliveryOptions, deliveryItems } = state.delivery;
              const { requestId: currentRequestId } = deliveryItems[0] || {};
              const optionIndex = deliveryOptions[orderKey]?.findIndex(
                (option) => option.deliveryType === deliveryTypes[0]
              );
              if (optionIndex >= 0 && retryDelivery) {
                return {
                  data: { ...data, expired: false },
                  orderId: orderKey,
                  optionIndex: optionIndex
                };
              }

              if (requestId === currentRequestId && !retryDelivery) {
                return { orderId: orderKey, data: { ...data, expired: false } };
              }
            },
            failureSelector: ({ data }) => {
              const state = getState();
              const { deliveryOptions, deliveryItems } = state.delivery;
              const optionIndex = deliveryOptions[orderKey]?.findIndex(
                (option) => option.deliveryType === deliveryTypes[0]
              );
              if (optionIndex >= 0 && retryDelivery) {
                return {
                  data: {
                    ...data,
                    deliveryType: deliveryTypes[0],
                    error: true
                  },
                  orderId: orderKey,
                  optionIndex: optionIndex
                };
              }

              if (!retryDelivery) {
                const { requestId: currentRequestId } = deliveryItems[0] || {};
                if (requestId === currentRequestId) {
                  return {
                    orderId: orderKey,
                    data: { ...data, deliveryType: type, error: true }
                  };
                }
              }
            },
            injections: [
              withPostSuccess((_, { data }) => {
                res(data);
              }),
              withPostFailure((_, { data }) => {
                rej(data);
              })
            ]
          });
        });
      })
    )
      .then((responses) => {
        if (!retryDelivery) {
          const minOption = getMinDeliveryOption(responses);
          if (minOption.type) {
            dispatch(
              actionCreators.setDeliveryTypeSelected({
                deliveryType: minOption.type,
                expireDate: minOption.expireDate,
                estimationId: minOption.estimationId
              })
            );
          }
        }
      })
      .finally(() => {
        if (!retryDelivery) {
          const state = getState();
          const { requestId: currentRequestId } = state.delivery.deliveryItems[0] || {};
          if (requestId === currentRequestId) {
            dispatch(actionCreators.setDeliveryLoading(ALL_DELIVERIES, false));
          }
          return;
        }

        dispatch(actionCreators.setDeliveryLoading(deliveryTypes[0], false));
      });
  },
  clearDeliveryEstimates: (id) => ({
    type: actions.CLEAR_DELIVERY_ESTIMATES,
    payload: { id },
    target: targets.DELIVERY_OPTIONS
  }),
  setDeliveryLoading: (delivery, isLoading) => ({
    type: actions.SET_DELIVERY_LOADING,
    payload: isLoading,
    target: `${delivery}IsLoading`
  }),
  setDeliveryTypeSelected: ({ deliveryType, expireDate, estimationId }) => ({
    type: actions.SET_DELIVERY_TYPE_SELECTED,
    payload: { type: deliveryType, expireDate, estimationId },
    target: targets.DELIVERY_TYPE_SELECTED
  }),
  setUpdatingDeliveryOrder: (orderId, deliveryType) => ({
    type: actions.SET_UPDATING_DELIVERY_ORDER,
    payload: { id: orderId, deliveryType },
    target: targets.ORDERS_UPDATING_DELIVERY
  }),
  setExpiredStatus: (orderId, deliveryType, isExpired) => (dispatch, getState) => {
    const orderKey = `order${orderId}`;
    const state = getState();
    const { deliveryOptions } = state.delivery;
    const optionIndex = deliveryOptions[orderKey].findIndex((option) => option.deliveryType === deliveryType);
    dispatch({
      type: actions.SET_EXPIRED_STATUS,
      payload: { optionIndex, isExpired },
      target: targets.DELIVERY_ITEM
    });
  },
  cancelDelivery: (deliveryId, values, onCancelSuccess, onCancelFailure) => ({
    type: actions.CANCEL_DELIVERY,
    payload: { deliveryId, values },
    service: DeliveryService.cancelDelivery,
    target: targets.CANCEL_DELIVERY,
    injections: [
      withPostSuccess(() => {
        onCancelSuccess({ ...values, deliveryCancelled: true });
      }),
      withPostFailure(() => {
        onCancelFailure();
      })
    ]
  })
};

export default actionCreators;
